import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Star, ArrowRight } from "lucide-react";
import { getIconComponent } from "./iconMap";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";

interface FeaturedToolsSectionProps {
  featuredTools: any[];
  loading?: boolean;
  onToolClick: (tool: any) => void;
}

export function FeaturedToolsSection({ featuredTools, loading, onToolClick }: FeaturedToolsSectionProps) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <Skeleton className="h-6 w-6 rounded" />
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-6 w-16 ml-auto" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {Array.from({ length: 3 }).map((_, idx) => (
            <Card key={idx} className="border bg-white">
              <CardHeader className="space-y-4">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-10 w-10 rounded-xl" />
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-4 rounded" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                </div>
                <div>
                  <Skeleton className="h-5 w-40 mb-2" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <Skeleton className="h-4 w-full" />
                <div className="flex gap-2">
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-16" />
                </div>
                <Skeleton className="h-9 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (featuredTools.length === 0) return null;

  return (
    <div className="mb-12">
      <div className="flex items-center gap-3 mb-6">
        <Star className="h-6 w-6 text-yellow-500 fill-current" />
        <h2 className="text-2xl font-bold">{t('homepage.featuredToolsGrid.title')}</h2>
          <Badge variant="secondary" className="ml-auto">{t('homepage.featuredToolsGrid.popular')}</Badge>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {featuredTools.slice(0, 3).map((tool) => {
          const IconComponent = getIconComponent(tool.icon);
          return (
            <Card
              key={tool.id}
              className="group hover:shadow-glow transition-all duration-300 cursor-pointer border bg-gradient-card hover:border-primary/40"
              onClick={() => onToolClick(tool)}
            >
              <CardHeader className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-primary/10 to-accent/10 text-primary group-hover:from-primary/20 group-hover:to-accent/20 transition-all duration-300">
                    <IconComponent className="h-7 w-7" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-yellow-500 fill-current" />
                    <Badge className="bg-primary text-primary-foreground text-xs">{t('homepage.featuredToolsGrid.featured')}</Badge>
                  </div>
                </div>
                <div>
                  <CardTitle className="text-lg group-hover:text-primary transition-colors mb-2">
                    {tool.name}
                  </CardTitle>
                  <Badge variant="outline" className="text-xs mb-2 border-jigso-light-blue/50">
                    {tool.category}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <CardDescription className="text-sm leading-relaxed">
                  {tool.description}
                </CardDescription>
                <div className="flex flex-wrap gap-1">
                  {tool.tags.slice(0, 3).map((tag: any) => (
                    <Badge key={`${tool.id}-${tag.name}`} variant="secondary" className="text-xs">
                      {tag.name}
                    </Badge>
                  ))}
                </div>
                <Button variant="gradient" className="w-full">
                  <div className="flex items-center gap-2">
                    {t('homepage.featuredToolsGrid.tryFeaturedTool')}
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
} 